import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Affiche un nom de medecin precede de son titre, SANS le dupliquer.
 *
 * Contexte : le nom stocke en base contient deja le titre dans la plupart des
 * cas — les 18 451 fiches importees sont enregistrees sous la forme
 * "Dr. Dahbia Amirouche", et les medecins qui s'inscrivent eux-memes le
 * saisissent souvent aussi. L'interface ajoutait par-dessus son propre
 * prefixe "Dr. " en dur, d'ou l'affichage "Dr. Dr. Dahbia Amirouche" — visible
 * seulement sur les fiches concernees, ce qui rendait le defaut intermittent.
 *
 * Un nom deja titre est renvoye tel quel ; sinon "Dr. " est ajoute.
 * Les professeurs gardent leur titre (on ne les retrograde pas en Dr.), et un
 * patronyme commencant par "Dr" sans en etre un titre — "Drissi" — n'est pas
 * confondu grace a la limite de mot.
 */
const TITRE_DEJA_PRESENT = /^(dr|docteur|pr|professeur)\b\.?/i;

export function formatDoctorName(name) {
  const nom = String(name ?? "").trim();
  if (!nom) return "";
  return TITRE_DEJA_PRESENT.test(nom) ? nom : `Dr. ${nom}`;
}

/**
 * Verifie qu'un numero de telephone est plausible avant de l'envoyer au
 * serveur (le serveur applique la meme regle cote backend/models.py, cette
 * copie cote client evite juste un aller-retour reseau pour une erreur de
 * saisie visible immediatement).
 *
 * Un simple comptage de chiffres (9-13) ne suffit pas : des numeros
 * algeriens deja vus en production comme "561250610" (0 initial manquant)
 * ou "+21356170681" (un chiffre manquant apres +213) tombent par
 * coincidence dans cette plage et passaient inapercus. On applique donc une
 * regle qui connait le format algerien :
 *   - numero local (commence par 0) -> doit faire exactement 10 chiffres
 *   - numero avec indicatif +213/00213 -> 213 + exactement 9 chiffres (12 au total)
 *   - tout le reste (diaspora, autre indicatif) -> plage large 10-15 chiffres
 *     (format international E.164) pour ne bloquer aucun numero etranger legitime.
 */
export function isValidPhone(value) {
  const raw = String(value ?? "").trim();
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }
  if (digits.startsWith("213")) {
    return digits.length === 12;
  }
  if (digits.startsWith("0")) {
    return digits.length === 10;
  }
  return digits.length >= 10 && digits.length <= 15;
}
